/**
 * Timezone utility functions for formatting dates in different timezones
 */

// Common timezone options for US healthcare practices
export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
];

// GH-92: client-side mirror of src/utils/timezone.js#isValidPracticeTimezone.
// Reject empty, UTC family, and any string the browser's Intl rejects. Used by
// Practice Settings to surface a red banner when a loaded practice has a bad
// tz, so the admin sees the misconfig instead of silently saving wrong-time
// notifications/voice prompts.
const UTC_TZ_ALIASES = new Set(['UTC', 'Etc/UTC', 'Etc/GMT', 'GMT', 'Zulu', 'Universal']);
export function isValidPracticeTimezone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  const trimmed = tz.trim();
  if (trimmed === '' || UTC_TZ_ALIASES.has(trimmed)) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: trimmed });
    return true;
  } catch {
    return false;
  }
}

// Display mode options
export const DISPLAY_MODES = {
  LOCAL: 'local',
  UTC: 'utc',
  PRACTICE: 'practice',
};

/**
 * Get the user's local timezone
 * @returns {string} IANA timezone identifier
 */
export function getLocalTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format a date/time string according to the specified display mode
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {string} displayMode - 'local', 'utc', or 'practice'
 * @param {string} practiceTimezone - IANA timezone for practice (e.g., 'America/New_York')
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date/time string
 */
export function formatDateTime(dateInput, displayMode, practiceTimezone, options = {}) {
  if (!dateInput) return '';

  // Appointment dates are stored as naive local time (practice timezone).
  // PostgreSQL DATE() serializes as "YYYY-MM-DDT00:00:00.000Z" which causes
  // timezone shift when parsed by the browser. For date-only display, parse
  // the date portion directly to avoid off-by-one day errors.
  if (typeof dateInput === 'string' && options.includeDate && !options.includeTime) {
    const datePart = dateInput.split('T')[0];
    const parts = datePart.split('-').map(Number);
    if (parts.length === 3 && parts[0] > 1900) {
      // Create date at noon UTC to avoid any DST edge cases
      const safeDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 12, 0, 0));
      if (!isNaN(safeDate.getTime())) {
        try {
          return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', dateStyle: options.dateStyle || 'medium' }).format(safeDate);
        } catch { /* fall through to default */ }
      }
    }
  }

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (isNaN(date.getTime())) return '';

  const {
    includeDate = true,
    includeTime = true,
    dateStyle = 'medium',
    timeStyle = 'short',
  } = options;

  let timezone;
  let suffix = '';

  switch (displayMode) {
    case DISPLAY_MODES.UTC:
      timezone = 'UTC';
      suffix = ' UTC';
      break;
    case DISPLAY_MODES.PRACTICE:
      timezone = practiceTimezone || 'America/New_York';
      suffix = ` (${getTimezoneAbbreviation(timezone, date)})`;
      break;
    case DISPLAY_MODES.LOCAL:
    default:
      timezone = getLocalTimezone();
      suffix = '';
      break;
  }

  const formatOptions = {
    timeZone: timezone,
  };

  if (includeDate && includeTime) {
    formatOptions.dateStyle = dateStyle;
    formatOptions.timeStyle = timeStyle;
  } else if (includeDate) {
    formatOptions.dateStyle = dateStyle;
  } else if (includeTime) {
    formatOptions.timeStyle = timeStyle;
  }

  try {
    const formatted = new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    return formatted + suffix;
  } catch (error) {
    console.error('Error formatting date:', error);
    return date.toLocaleString();
  }
}

/**
 * Format just the date portion
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {string} displayMode - 'local', 'utc', or 'practice'
 * @param {string} practiceTimezone - IANA timezone for practice
 * @returns {string} Formatted date string
 */
export function formatDate(dateInput, displayMode, practiceTimezone) {
  return formatDateTime(dateInput, displayMode, practiceTimezone, {
    includeDate: true,
    includeTime: false,
  });
}

/**
 * Format just the time portion
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {string} displayMode - 'local', 'utc', or 'practice'
 * @param {string} practiceTimezone - IANA timezone for practice
 * @returns {string} Formatted time string
 */
export function formatTime(dateInput, displayMode, practiceTimezone) {
  return formatDateTime(dateInput, displayMode, practiceTimezone, {
    includeDate: false,
    includeTime: true,
  });
}

/**
 * Get the abbreviation for a timezone (e.g., EST, PST)
 * @param {string} timezone - IANA timezone identifier
 * @param {Date} date - Date to determine DST status
 * @returns {string} Timezone abbreviation
 */
export function getTimezoneAbbreviation(timezone, date = new Date()) {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart ? tzPart.value : timezone;
  } catch (error) {
    return timezone;
  }
}

/**
 * Get a human-readable label for the display mode
 * @param {string} displayMode - 'local', 'utc', or 'practice'
 * @param {string} practiceTimezone - IANA timezone for practice
 * @returns {string} Human-readable label
 */
export function getDisplayModeLabel(displayMode, practiceTimezone) {
  switch (displayMode) {
    case DISPLAY_MODES.UTC:
      return 'UTC';
    case DISPLAY_MODES.PRACTICE:
      return `Practice (${getTimezoneAbbreviation(practiceTimezone || 'America/New_York')})`;
    case DISPLAY_MODES.LOCAL:
    default:
      return `Local (${getTimezoneAbbreviation(getLocalTimezone())})`;
  }
}

/**
 * Get the timezone label for display in dropdowns
 * @param {string} timezoneValue - IANA timezone identifier
 * @returns {string} Human-readable timezone label
 */
export function getTimezoneLabel(timezoneValue) {
  const option = TIMEZONE_OPTIONS.find(opt => opt.value === timezoneValue);
  return option ? option.label : timezoneValue;
}
